"use client";

import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { getWPBaseUrl } from "@/config/site";
import { usePathname } from "next/navigation";
import IconBox from "@/components/icon-box";
import { useTranslations } from "next-intl";

// Mapping untuk icon berdasarkan slug atau kategori
const iconMapping = {
    "web-development": "/icons/ikon-layanan-web.svg",
    "digital-marketing": "/icons/ikon-layanan-digital-marketing.svg",
    "content-creator": "/icons/ikon-layanan-content-creator.svg",
    "mobile-app-development": "/icons/ikon-layanan-mobile-app.svg",
    "ui-ux": "/icons/ikon-layanan-ui-ux.svg",
    "graphics-design": "/icons/ikon-layanan-graphics-designer.svg",
    "seo": "/icons/ikon-layanan-seo.svg",
    "hosting": "/icons/ikon-layanan-server.svg",
    "whatsapp": "/icons/whatsapp.svg",
    "qontak": "/icons/ikon-layanan-crm.svg", // Icon untuk Mubarokah Qontak
    "mubarokah-qontak": "/icons/ikon-layanan-crm.svg", // Alternative mapping

    "jasa-pembuatan-website": "/icons/ikon-layanan-web.svg",
    "jasa-digital-marketing": "/icons/ikon-layanan-digital-marketing.svg",
    "jasa-pembuatan-konten": "/icons/ikon-layanan-content-creator.svg",
    "jasa-pembuatan-aplikasi": "/icons/ikon-layanan-mobile-app.svg",
    "desain-ui-ux": "/icons/ikon-layanan-ui-ux.svg",
    "jasa-desain-grafis": "/icons/ikon-layanan-graphics-designer.svg",
    "jasa-seo": "/icons/ikon-layanan-seo.svg",
    "layanan-hosting": "/icons/ikon-layanan-server.svg",
    "layanan-whatsapp-bisnis-api": "/icons/whatsapp.svg",
    "platform-crm-omnichannel": "/icons/ikon-layanan-crm.svg", // Mapping Indonesia
} as const;

// Interface untuk service data
interface ServiceData {
    icon: string;
    title: string;
    description: string;
    url: string;
    isManual?: boolean;
    priority?: number;
}

// Helper function untuk mendapatkan icon berdasarkan slug atau kategori
const getIconBySlug = (slug: string, categories: any[]): string => {
    // Coba dapatkan icon berdasarkan slug
    const slugKey = Object.keys(iconMapping).find(key => slug.includes(key));
    if (slugKey) {
        return iconMapping[slugKey as keyof typeof iconMapping];
    }

    // Jika tidak ada di slug, coba cari di kategori
    if (categories && categories.length > 0) {
        const categoryKey = Object.keys(iconMapping).find(key =>
            categories.some(cat => cat.slug.includes(key))
        );
        if (categoryKey) {
            return iconMapping[categoryKey as keyof typeof iconMapping];
        }
    }

    // Default icon jika tidak ada yang cocok
    return iconMapping["web-development"];
};

// Function untuk mendapatkan layanan manual
const getManualServices = (locale: string): ServiceData[] => {
    const isIndonesian = locale === 'id';
    
    return [
        {
            icon: iconMapping["mubarokah-qontak"],
            title: isIndonesian 
                ? "Mubarokah Qontak - Platform CRM Omnichannel" 
                : "Mubarokah Qontak - Omnichannel CRM Platform",
            description: isIndonesian
                ? "Revolusionize customer engagement dengan platform CRM omnichannel terdepan. WhatsApp Business API, AI chatbot, dan sales automation untuk pertumbuhan bisnis optimal."
                : "Revolutionize customer engagement with the leading omnichannel CRM platform. WhatsApp Business API, AI chatbot, and sales automation for optimal business growth.",
            url: "/services/mubarokah-qontak",
            isManual: true,
            priority: 1 // Akan ditampilkan di urutan pertama
        }
        // Tambahkan layanan manual lainnya di sini jika diperlukan
    ];
};

const fadeInAnimationVariants = {
    initial: {
        opacity: 0,
        y: 60,
    },
    animate: (index: number) => ({
        opacity: 1,
        y: 0,
        transition: {
            delay: 0.05 * index,
        },
    }),
};

const DynamicServiceSection = ({ noTitle }: { noTitle?: boolean }) => {
    const t = useTranslations("SectionServicesDynamicIconBoxes");
    const [services, setServices] = useState<ServiceData[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const pathname = usePathname();
    const locale = pathname.startsWith("/id") ? "id" : "en";
    const WP_REST_BASE_URL = getWPBaseUrl(locale);

    useEffect(() => {
        const fetchServices = async () => {
            try {
                setError(null);
                
                // Dapatkan layanan manual terlebih dahulu
                const manualServices = getManualServices(locale);
                
                // Fetch data dari WordPress API
                const response = await fetch(
                    `${WP_REST_BASE_URL}/service-details?_embed&per_page=100`
                );
                
                let wpServices: ServiceData[] = [];
                
                if (response.ok) {
                    const data = await response.json();
                    
                    // Transform WordPress data dengan icon yang sesuai
                    wpServices = data.map((service: any, index: number) => {
                        const categories = service._embedded?.["wp:term"]?.[0] || [];
                        const icon = getIconBySlug(service.slug, categories);

                        return {
                            icon: icon,
                            title: service.title.rendered.replace(/&amp;/g, '&'),
                            description: service.excerpt.rendered.replace(/<[^>]+>/g, ''), // Remove HTML tags
                            url: `/services/${service.slug}`,
                            isManual: false,
                            priority: index + 10 // Prioritas lebih rendah dari manual services
                        };
                    });
                } else {
                    console.warn('Failed to fetch WordPress services, using manual services only');
                    setError('Some services may not be displayed due to connection issues.');
                }
                
                // Gabungkan layanan manual dengan WordPress services
                const allServices = [...manualServices, ...wpServices];
                
                // Urutkan berdasarkan priority
                const sortedServices = allServices.sort((a, b) => {
                    const priorityA = a.priority || 999;
                    const priorityB = b.priority || 999;
                    return priorityA - priorityB;
                });
                
                setServices(sortedServices);
                setIsLoading(false);
                
            } catch (error) {
                console.error("Error fetching services:", error);
                setError('Connection issue detected. Showing available services.');
                
                // Fallback: gunakan hanya layanan manual jika API gagal
                const fallbackServices = getManualServices(locale);
                setServices(fallbackServices);
                setIsLoading(false);
            }
        };
        
        fetchServices();
    }, [WP_REST_BASE_URL, locale]);

    // Loading state dengan skeleton yang lebih baik
    if (isLoading) {
        return (
            <section className="py-16 lg:py-24">
                <div className="container">
                    {!noTitle && (
                        <div className="flex justify-center mb-12">
                            <div className="text-center lg:w-3/5">
                                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded-lg w-3/4 mx-auto mb-4 animate-pulse"></div>
                                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-lg w-1/2 mx-auto animate-pulse"></div>
                            </div>
                        </div>
                    )}
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3 lg:gap-10">
                        {[...Array(6)].map((_, i) => (
                            <div key={i} className="animate-pulse">
                                <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="py-16 lg:py-24">
            <div className="container">
                {!noTitle && (
                    <div className="flex justify-center">
                        <div className="text-center lg:w-3/5">
                            <h2 className="mb-12">
                                {t("title1")} <span className="text-primary">{t("title2")}</span> {t("title3")}
                            </h2>
                        </div>
                    </div>
                )}

                {/* Error notification jika ada masalah dengan API */}
                {error && (
                    <div className="mb-8 text-center">
                        <div className="inline-block bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-200 px-4 py-2 rounded-lg text-sm">
                            {error}
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 gap-4 lg:grid-cols-3 lg:gap-10">
                    {services.map((service, index) => (
                        <motion.div
                            key={`${service.title}-${index}`}
                            variants={fadeInAnimationVariants}
                            initial="initial"
                            whileInView="animate"
                            viewport={{ once: true }}
                            custom={index}
                            className={service.isManual ? "relative" : ""}
                        >
                            {/* Badge untuk layanan manual */}
                            {/* {service.isManual && (
                                <div className="absolute top-0 right-0 z-10 transform translate-x-2 -translate-y-2">
                                    <div className="bg-gradient-to-r from-blue-500 to-purple-500 text-white text-xs font-semibold px-3 py-1 rounded-full shadow-lg">
                                        {locale === 'id' ? 'Terpopuler' : 'Popular'}
                                    </div>
                                </div>
                            )} */}
                            
                            <div className={service.isManual ? "rounded-lg" : ""}>
                                <IconBox iconBox={service} />
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* Tampilkan informasi tambahan jika hanya ada layanan manual */}
                {services.length === 1 && services[0].isManual && (
                    <div className="mt-12 text-center">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            {locale === 'id' 
                                ? 'Layanan lainnya sedang dimuat. Silakan refresh halaman atau coba lagi nanti.'
                                : 'Other services are loading. Please refresh the page or try again later.'
                            }
                        </p>
                    </div>
                )}
            </div>
        </section>
    );
};

export default DynamicServiceSection;